import React from "react";
import { WORKFORCE_PERSONAS } from "../../../../../convex/aiWorkforce/config";
import { getPersonaTheme } from "./personaTheme";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Bot,
  ExternalLink,
  Clock,
  CheckCircle2,
  XCircle,
  FileText,
  FileSpreadsheet,
  FileCode,
} from "lucide-react";

export interface WorkDeliverableCardProps {
  artifact: {
    _id: string;
    threadId: string;
    title: string;
    type: string;
    createdPersona: string;
    status: string;
    createdAt: number;
    data?: any;
  };
  isSelected?: boolean;
  onOpen: (artifactId: string, threadId: string) => void;
  compact?: boolean;
}

export function formatArtifactType(type: string): string {
  switch (type) {
    case "lead_batch_preview":
      return "Leads Batch";
    case "work_order_draft":
      return "Work Order";
    case "invoice_preview":
      return "Invoice Draft";
    case "shift_grid":
      return "Shift Grid";
    case "hr_onboarding_packet":
      return "HR Packet";
    case "client_message_draft":
      return "Client Message";
    case "document_pdf":
    case "commercial_proposal":
      return "PDF Document";
    case "analytical_report":
    case "markdown_report":
      return "Research Brief";
    default:
      return type.replace(/_/g, " ");
  }
}

export function getArtifactIcon(type: string) {
  switch (type) {
    case "document_pdf":
    case "commercial_proposal":
      return FileText;
    case "shift_grid":
    case "lead_batch_preview":
      return FileSpreadsheet;
    case "analytical_report":
    case "markdown_report":
      return FileCode;
    default:
      return FileText;
  }
}

export function WorkDeliverableCard({
  artifact,
  isSelected = false,
  onOpen,
  compact = false,
}: WorkDeliverableCardProps) {
  const persona = WORKFORCE_PERSONAS[artifact.createdPersona] || WORKFORCE_PERSONAS.chief_of_staff;
  const theme = getPersonaTheme(persona.key);
  const IconComponent = getArtifactIcon(artifact.type);

  return (
    <div
      onClick={() => onOpen(artifact._id, artifact.threadId)}
      className={`group rounded-xl border transition-all flex items-center justify-between gap-3 cursor-pointer shadow-xs ${
        compact ? "p-3" : "p-4"
      } ${
        isSelected
          ? "bg-blue-50/80 border-blue-300 shadow-sm"
          : "bg-white border-gray-200 hover:border-blue-300 hover:bg-slate-50/70"
      }`}
    >
      <div className="flex items-center gap-3 min-w-0">
        {/* Agent Themed Avatar */}
        <div
          className={`rounded-xl ${theme.bgLight} border ${theme.border} ${theme.text} flex items-center justify-center flex-shrink-0 shadow-2xs ${
            compact ? "w-8 h-8" : "w-10 h-10"
          }`}
        >
          <Bot className={compact ? "w-4 h-4" : "w-5 h-5"} />
        </div>

        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-sm font-bold text-gray-900 group-hover:text-blue-600 transition-colors truncate">
              {artifact.title}
            </h4>
            <Badge
              variant="outline"
              className="text-2xs font-semibold uppercase tracking-wider py-0 px-1.5 text-gray-600 bg-gray-50 border-gray-200"
            >
              {formatArtifactType(artifact.type)}
            </Badge>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
            <span className="font-semibold text-gray-700">{persona.name}</span>
            <span>•</span>
            <span>
              {new Date(artifact.createdAt).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2.5 flex-shrink-0">
        {/* Review Status Pill */}
        {artifact.status === "pending_approval" && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-800 border border-amber-200">
            <Clock className="w-3.5 h-3.5 text-amber-600" />
            <span className="hidden sm:inline">Review Needed</span>
            <span className="sm:hidden">Review</span>
          </span>
        )}
        {artifact.status === "approved" && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            <span>Approved</span>
          </span>
        )}
        {artifact.status === "draft" && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
            <span>Draft</span>
          </span>
        )}
        {artifact.status === "rejected" && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-rose-50 text-rose-700 border border-rose-200">
            <XCircle className="w-3.5 h-3.5 text-rose-600" />
            <span>Declined</span>
          </span>
        )}

        {/* Distinct Open Action Button */}
        <Button
          variant="outline"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onOpen(artifact._id, artifact.threadId);
          }}
          className="h-8 px-2.5 text-xs font-semibold gap-1 text-blue-600 border-blue-200 bg-blue-50/50 hover:bg-blue-600 hover:text-white transition-all shadow-2xs"
        >
          <span>Open</span>
          <ExternalLink className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}
